/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Stepper,
    Step,
    StepLabel,
    StepConnector,
    CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { updateAIConfig } from 'src/redux/slices/whatsappV2';
import { updateError } from 'src/redux/slices/user';
import WelcomeStep from './steps/WelcomeStep';
import OutOfHoursStep from './steps/OutOfHoursStep';
import TreatmentStep from './steps/TreatmentStep';
import AutoRepliesStep from './steps/AutoRepliesStep';
import ToneStep from './steps/ToneStep';
import TestDriveStep from './steps/TestDriveStep';
import ReviewStep from './steps/ReviewStep';

const STEPS = [
    { id: 'welcome', label: 'Welcome' },
    { id: 'outhours', label: 'Out of Hours' },
    { id: 'treatments', label: 'Treatments' },
    { id: 'autoreplies', label: 'Auto-Replies' },
    { id: 'tone', label: 'Tone' },
    { id: 'testdrive', label: 'Test Drive' },
    { id: 'summary', label: 'Review' },
];

const DEFAULT_FORM_DATA = {
    auto_reply_scope: 'new_leads_only',
    out_of_hours_enabled: false,
    out_of_hours_message: 'Hey {{first_name}} 👋 Thanks so much for reaching out to {{practice_name}}!\n\nWe\'re currently closed but don\'t worry — we\'ll get back to you as soon as we open at {{next_open_time}}.\n\nIf it\'s urgent, please give us a call on {{practice_phone}} 😊',
    weekend_same_message: false,
    bank_holiday_detect: false,
    auto_assign_treatment: true,
    auto_send_treatment_template: false,
    treatment_message: 'Hey {{first_name}} 😄 Thanks for your interest in {{treatment_name}} at {{practice_name}}!\n\nOne of our lovely team will be in touch with you shortly 😊',
    unmatched_treatment_action: 'notify_team',
    multi_treatment_action: 'assign_first',
    appt_ack_enabled: false,
    appt_message: 'Hey {{first_name}} 👋 Thanks for getting in touch about your appointment!\n\nA member of our team will get back to you shortly 😊',
    appt_ack_style: 'auto',
    send_booking_link: false,
    booking_link_type: 'calendly',
    auto_reply_hours: false,
    hours_message: 'Hey there! 👋 Our opening hours are:\n\n{{opening_hours}}\n\nFeel free to give us a call or pop in during these times 😊',
    auto_reply_location: false,
    location_message: 'Hey! 😄 You can find us at:\n\n{{practice_address}}\n\nHere\'s a Google Maps link to help you find us: {{maps_link}} 📍',
    auto_reply_pricing: false,
    pricing_action: 'ask_consult',
    existing_ack_enabled: false,
    existing_message: 'Hey {{first_name}} 👋 Thanks for getting in touch!\n\nYour care team will review your message and get back to you shortly 😊',
    existing_route: 'any',
    existing_flag_urgent: false,
    urgent_detect: false,
    urgent_message: 'Hey {{first_name}}, we\'re really sorry to hear you\'re in discomfort 💙\n\nWe\'ll get someone to call you back as soon as possible.\n\nIf you need immediate help, please call us on {{practice_phone}} or contact NHS 111 🙏',
    urgent_alert_target: 'any',
    urgent_send_emergency_info: false,
    sentiment_detect: false,
    sentiment_alert_manager: false,
    ai_tone: 'warm_professional',
    sign_off_name: 'practice',
    use_emoji: false,
};

// Custom stepper connector
const GreenConnector = styled(StepConnector)({
    '& .MuiStepConnector-line': {
        borderColor: '#EBEBEB',
        borderTopWidth: 2,
    },
    '&.Mui-active .MuiStepConnector-line, &.Mui-completed .MuiStepConnector-line': {
        borderColor: '#00A76F',
    },
});

// Custom step icon
function StepIcon({ active, completed, icon }) {
    return (
        <Box sx={{
            width: 28, height: 28, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700,
            backgroundColor: (completed || active) ? '#00A76F' : '#EBEBEB',
            color: completed || active ? '#fff' : '#B0B0B0',
            transition: 'all 0.2s',
        }}>
            {completed ? '\u2713' : icon}
        </Box>
    );
}

StepIcon.propTypes = {
    active: PropTypes.bool,
    completed: PropTypes.bool,
    icon: PropTypes.node,
};

export default function AISetupWizard({ onClose, practiceId, initialConfig }) {
    const dispatch = useDispatch();
    const [currentStep, setCurrentStep] = React.useState(0);
    const [saving, setSaving] = React.useState(false);
    const [formData, setFormData] = React.useState(DEFAULT_FORM_DATA);

    // Get practice name and logo from Redux
    const practiceList = useSelector((state) => state?.practiceTreatmentList?.practiceTreatment?.results);
    const currentPractice = practiceList?.find((p) => String(p.id) === String(practiceId));
    const practiceName = currentPractice?.name || '';
    const practiceLogo = currentPractice?.logo || '';
    const practicePhone = currentPractice?.phone || '';
    const practiceAddress = currentPractice?.address
        ? [currentPractice.address.address_line_1, currentPractice.address.address_line_2, currentPractice.address.city, currentPractice.address.postcode].filter(Boolean).join(', ')
        : '';
    const practiceOpeningHours = currentPractice?.opening_hours?.length
        ? currentPractice.opening_hours.map((h) => `${h.day}: ${h.open_time || 'Closed'}${h.close_time ? ` - ${h.close_time}` : ''}`).join('\n')
        : '';

    React.useEffect(() => {
        if (initialConfig) {
            setFormData((prev) => ({ ...prev, ...initialConfig }));
        }
    }, []);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    // Save and move to next step
    const handleSaveAndContinue = async () => {
        if (!practiceId || currentStep >= STEPS.length - 1) return;
        setSaving(true);
        // Critical: DO NOT send wizard_completed here. Sending `false` would
        // un-mark a previously-completed wizard every time the operator
        // re-opens it to tweak a setting on an intermediate step. Only the
        // final "Save and Activate" step sets wizard_completed = true; all
        // other steps leave the flag untouched (PATCH preserves it).
        const payload = { ...formData };
        delete payload.wizard_completed;
        const result = await dispatch(updateAIConfig(practiceId, payload));
        setSaving(false);
        if (result?.ok) {
            setCurrentStep((prev) => prev + 1);
        } else {
            dispatch(updateError({ message: result?.error || 'Failed to save. Please try again.', success: false }));
        }
    };

    const handleNext = () => {
        if (currentStep < STEPS.length - 1) {
            setCurrentStep((prev) => prev + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep((prev) => prev - 1);
        }
    };

    const handleSaveAndActivate = async () => {
        if (!practiceId) return;
        setSaving(true);
        const result = await dispatch(updateAIConfig(practiceId, { ...formData, wizard_completed: true }));
        setSaving(false);
        if (result?.ok) {
            dispatch(updateError({ message: 'WhatsApp AI configuration saved and activated!', success: true }));
            onClose();
        } else {
            dispatch(updateError({ message: result?.error || 'Failed to save configuration. Please try again.', success: false }));
        }
    };

    const isLastStep = currentStep === STEPS.length - 1;
    const isFirstStep = currentStep === 0;

    const practiceProps = { practiceName, practiceLogo, practicePhone, practiceAddress, practiceOpeningHours };

    const renderStep = () => {
        switch (currentStep) {
            case 0: return <WelcomeStep formData={formData} onChange={handleChange} />;
            case 1: return <OutOfHoursStep formData={formData} onChange={handleChange} {...practiceProps} />;
            case 2: return <TreatmentStep formData={formData} onChange={handleChange} {...practiceProps} />;
            case 3: return <AutoRepliesStep formData={formData} onChange={handleChange} {...practiceProps} />;
            case 4: return <ToneStep formData={formData} onChange={handleChange} />;
            case 5: return <TestDriveStep practiceId={practiceId} />;
            case 6: return <ReviewStep formData={formData} />;
            default: return null;
        }
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fff',
            // Hug the content rather than the parent panel — short steps
            // (e.g. Welcome) no longer leave a wide empty band above the
            // footer. Long steps still scroll inside their own content box.
            borderRadius: '12px',
            overflow: 'hidden',
            border: '1px solid rgba(145, 158, 171, 0.16)',
        }}>
            {/* Stepper header */}
            <Box sx={{ px: 2, pt: 2, pb: 1, borderBottom: '1px solid #F0F0F0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#212B36' }}>
                        WhatsApp AI Setup
                    </Typography>
                    <Box
                        component="button"
                        onClick={onClose}
                        sx={{
                            display: 'flex', alignItems: 'center',
                            cursor: 'pointer', border: 'none', background: 'none',
                            p: 0, color: '#919EAB',
                            '&:hover': { color: '#637381' },
                        }}
                    >
                        <Icon icon="mdi:close" width={18} height={18} />
                    </Box>
                </Box>
                <Stepper
                    activeStep={currentStep}
                    alternativeLabel
                    connector={<GreenConnector />}
                    sx={{ '& .MuiStepLabel-label': { fontSize: 10, mt: 0.5 } }}
                >
                    {STEPS.map((step) => (
                        <Step key={step.id}>
                            <StepLabel StepIconComponent={StepIcon} sx={{
                                '& .MuiStepLabel-label': {
                                    fontSize: '0.6rem',
                                    color: '#919EAB',
                                    '&.Mui-active': { color: '#00A76F', fontWeight: 600 },
                                    '&.Mui-completed': { color: '#00A76F' },
                                },
                            }}>
                                {step.label}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Box>

            {/* Step content — natural height (no flex:1) so the footer sits
                immediately under the rendered step. Long steps cap to a max
                height with internal scroll so the wizard never pushes the
                footer off-screen. */}
            <Box sx={{
                px: 3, pt: 2, pb: 1.5,
                maxHeight: '60vh',
                overflowY: 'auto',
            }}>
                {renderStep()}
            </Box>

            {/* Footer buttons */}
            <Box sx={{
                px: 2, py: 1,
                borderTop: '1px solid #F0F0F0',
                display: 'flex', alignItems: 'center', gap: 1,
                backgroundColor: '#fff',
                flexShrink: 0,
            }}>
                {!isFirstStep && (
                    <Box
                        component="button"
                        onClick={handleBack}
                        disabled={saving}
                        sx={{
                            px: 2, py: 1,
                            borderRadius: '10px',
                            border: '1.5px solid #EBEBEB',
                            background: '#fff',
                            cursor: 'pointer',
                            fontSize: 12, fontWeight: 600,
                            color: '#637381',
                            fontFamily: 'inherit',
                            '&:hover': { borderColor: '#ccc', background: '#FAFAFA' },
                        }}
                    >
                        Back
                    </Box>
                )}
                <Box
                    component="button"
                    /* eslint-disable-next-line no-nested-ternary */
                    onClick={isFirstStep ? handleNext : isLastStep ? handleSaveAndActivate : handleSaveAndContinue}
                    disabled={saving}
                    sx={{
                        flex: 1,
                        px: 2, py: 1,
                        borderRadius: '10px',
                        border: 'none',
                        background: '#00A76F',
                        color: '#fff',
                        fontSize: 12, fontWeight: 600,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        fontFamily: 'inherit',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                        '&:hover': { background: '#007B55' },
                    }}
                >
                    {saving && <CircularProgress size={12} sx={{ color: 'white' }} />}
                    {/* eslint-disable-next-line no-nested-ternary */}
                    {isFirstStep ? 'Get Started' : isLastStep ? (saving ? 'Saving...' : 'Save & Activate') : (saving ? 'Saving...' : 'Save & Continue')}
                </Box>
                {!isFirstStep && !isLastStep && (
                    <Box
                        component="button"
                        onClick={handleNext}
                        disabled={saving}
                        sx={{
                            px: 2, py: 1,
                            borderRadius: '10px',
                            border: '1.5px solid #EBEBEB',
                            background: '#fff',
                            cursor: 'pointer',
                            fontSize: 12, fontWeight: 600,
                            color: '#919EAB',
                            fontFamily: 'inherit',
                            '&:hover': { borderColor: '#ccc', color: '#637381', background: '#FAFAFA' },
                        }}
                    >
                        Skip
                    </Box>
                )}
            </Box>
        </Box>
    );
}

AISetupWizard.propTypes = {
    onClose: PropTypes.func.isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    initialConfig: PropTypes.object,
};

AISetupWizard.defaultProps = {
    practiceId: null,
    initialConfig: null,
};
